import {
  type FC,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import DateTimeInputInline from '__components/Inputs/inline/DateTimeInput';
import SelectInline from '__components/Inputs/inline/Select';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import { SizedCell } from '__components/Kizen/Table/CellLineHeightStyles';
import ValidatedField from '__components/Kizen/ValidatedField';
import {
  type AutomationOrFolder,
  type AutomationWithCustomObjectAccess,
  updatePageConfig,
  type Automation,
  type Folder,
} from 'store/automationsPage/automations.redux';
import { formatHeaderAndDescriptionToolTip } from '__components/Kizen/Tooltip';
import { useDispatch, useSelector } from 'react-redux';
import { ResizerLayout } from '__pages/Common/components/BigTableCells';
import { DraggableCore, type DraggableEventHandler } from 'react-draggable';
import Icon from '__components/Kizen/Icon';
import { Icon as KDSIcon } from '@kizen/kds/Icon';
import {
  type AutomationColumnId,
  CELL_PADDING,
  ColumnsContext,
  minColumnWidth,
} from './columnsContext';
import TeamMemberService from 'services/TeamMemberService';
import { isTypeGlobal, isTypeRecordBased } from './helpers';
import {
  StyledActionCell,
  StyledAutomationFolder,
  StyledCounts,
  StyledFolderLink,
  StyledNoActionsCell,
  StyledSortableHeadCell,
} from './styles';
import { useDragAndDropContext } from './FoldersDragAndDrop/context';

type OnUpdateAutomation = (
  data: Partial<Omit<Automation, 'revision'>> & { lastRevision: number }
) => Promise<void>;

type CellProps = {
  column: {
    id: keyof AutomationWithCustomObjectAccess;
    cell: ReactNode;
    headCell: ReactNode;
  };
  data: AutomationOrFolder & { value: any };
  head?: boolean;
};

export const columnSize = {
  spacer: '20px',
  actions: '47px',
};

export const paddingSize = {
  small: '20px',
  big: `${CELL_PADDING}px`,
};

export const actions = {
  edit: 'edit',
  edit_automation_folder: 'edit_automation_folder',
  edit_description: 'edit_description',
  deactivate: 'deactivate',
  activate: 'activate',
  duplicate: 'duplicate',
  history: 'history',
  delete: 'delete',
  edit_folder: 'edit_folder',
  delete_folder: 'delete_folder',
  run: 'run',
  new_automation: 'new_automation',
  new_folder: 'new_folder',
} as const;

export const statusOptions = (t: (x: string) => string) => [
  {
    value: 'true',
    label: t('Active'),
    color: 'var(--color-viz-cat-1-primary)',
  },
  {
    value: 'false',
    label: t('Inactive'),
    color: 'var(--color-viz-cat-3-primary)',
  },
];

const isAutomationData = (
  data: any
): data is AutomationWithCustomObjectAccess => {
  return data?.folder !== undefined;
};

type AutomationFolderProps = {
  folderName: string;
  onFolderClick: () => void;
  automationsCount?: number;
};

const AutomationFolder = ({
  onFolderClick,
  folderName,
  automationsCount,
}: AutomationFolderProps) => {
  const { columnsConfig } = useContext(ColumnsContext);
  const { dragging, patching } = useDragAndDropContext();

  return (
    <SizedCell
      width={`${columnsConfig.name.width}px`}
      padding={paddingSize.big}
    >
      <StyledAutomationFolder
        onClick={dragging || patching ? undefined : onFolderClick}
      >
        <KDSIcon
          icon="custom-folder"
          color="button-secondary-default"
          size="base"
          className="shrink-0"
        />
        <StyledFolderLink type="link">{folderName}</StyledFolderLink>
        <StyledCounts fontStyle="italic">
          ({automationsCount ?? 0})
        </StyledCounts>
      </StyledAutomationFolder>
    </SizedCell>
  );
};

export const NameCell = ({
  column,
  data,
  onChangeDirectory,
  onUpdateAutomation,
  head: _h = false,
}: CellProps & {
  onUpdateAutomation: OnUpdateAutomation;
  onChangeDirectory: (data: Folder) => void;
}) => {
  const { columnsConfig } = useContext(ColumnsContext);

  if (!isAutomationData(data)) {
    const onFolderClick = () => onChangeDirectory(data);

    return (
      <AutomationFolder
        folderName={data.name}
        automationsCount={data.automations_count ?? 0}
        onFolderClick={onFolderClick}
      />
    );
  }

  const value = data[column.id];

  const handleSubmit = async (v: string) => {
    if (v !== value) {
      await onUpdateAutomation({
        id: data.id,
        name: v,
        lastRevision: data.revision,
      });
    }
  };

  if (data.customObject.access.view || isTypeGlobal(data)) {
    const { userDescription } = data;
    return (
      <SizedCell
        width={`${columnsConfig.name.width}px`}
        padding={paddingSize.big}
      >
        <ValidatedField
          type="link"
          to={`/agentic-workflow/${data.id}/`}
          value={value}
          onSubmit={handleSubmit}
          tooltipOverride={
            userDescription
              ? formatHeaderAndDescriptionToolTip(
                  value?.toString() ?? '',
                  userDescription
                )
              : undefined
          }
        />
      </SizedCell>
    );
  }

  return (
    <SizedCell
      width={`${columnsConfig.name.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const ObjectNameCell = ({
  column,
  data,
  head: _h = false,
}: CellProps) => {
  const { columnsConfig } = useContext(ColumnsContext);

  const value = isAutomationData(data) ? data[column.id] : null;

  return (
    <SizedCell
      width={`${columnsConfig.customObjectName.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const StatusCell = ({
  column,
  data,
  onUpdateAutomation,
  head: _h = false,
  canEdit = false,
}: CellProps & {
  onUpdateAutomation: OnUpdateAutomation;
  canEdit?: boolean;
}) => {
  const { t } = useTranslation();
  const { columnsConfig } = useContext(ColumnsContext);

  if (!isAutomationData(data)) {
    return (
      <SizedCell
        width={`${columnsConfig.status.width}px`}
        padding={paddingSize.big}
      >
        <TextEllipsisWithTooltip />
      </SizedCell>
    );
  }

  const value = data[column.id];
  const options = statusOptions(t);
  const option = options.find((opt) => opt.value === String(value));

  return (
    <SizedCell
      width={`${columnsConfig.status.width}px`}
      padding={paddingSize.big}
    >
      <SelectInline
        readOnly={
          !canEdit || (!data.customObject.access.view && !isTypeGlobal(data))
        }
        onSubmit={async (opt: any) => {
          await onUpdateAutomation({
            id: data.id,
            active: opt.value === 'true',
            lastRevision: data.revision,
          });
        }}
      >
        <Select
          value={option}
          options={options}
          showClearButton={false}
          isColored
        />
      </SelectInline>
    </SizedCell>
  );
};

export const NumberActiveCell = ({
  column,
  data,
  head: _h = false,
}: CellProps) => {
  const { columnsConfig } = useContext(ColumnsContext);

  const value = isAutomationData(data) ? data[column.id] : null;

  return (
    <SizedCell
      width={`${columnsConfig.numberActive.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const NumberPausedCell = ({
  column,
  data,
  head: _h = false,
}: CellProps) => {
  const { columnsConfig } = useContext(ColumnsContext);

  const value = isAutomationData(data) ? data[column.id] : null;

  return (
    <SizedCell
      width={`${columnsConfig.numberPaused.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const NumberCompletedCell = ({
  column,
  data,
  head: _h = false,
}: CellProps) => {
  const { columnsConfig } = useContext(ColumnsContext);

  const value = isAutomationData(data) ? data[column.id] : null;

  return (
    <SizedCell
      width={`${columnsConfig.numberCompleted.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>{value}</TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const TypeCell = ({ data, head: _h = false }: CellProps) => {
  const { t } = useTranslation();
  const { columnsConfig } = useContext(ColumnsContext);

  return (
    <SizedCell
      width={`${columnsConfig.type.width}px`}
      padding={paddingSize.big}
    >
      <TextEllipsisWithTooltip>
        {isAutomationData(data)
          ? isTypeRecordBased(data)
            ? t('Record-Based')
            : t('Global')
          : t('Folder')}
      </TextEllipsisWithTooltip>
    </SizedCell>
  );
};

export const CreatedCell = ({ column, data, head: _h = false }: CellProps) => {
  const { columnsConfig } = useContext(ColumnsContext);

  if (!isAutomationData(data)) {
    return (
      <SizedCell
        width={`${columnsConfig.created.width}px`}
        padding={paddingSize.big}
      >
        <TextEllipsisWithTooltip />
      </SizedCell>
    );
  }

  return (
    <SizedCell
      width={`${columnsConfig.created.width}px`}
      padding={paddingSize.big}
    >
      <DateTimeInputInline readOnly value={data[column.id]} showDateTooltip />
    </SizedCell>
  );
};

const POPPER_CONFIG = {
  positionFixed: true,
  modifiers: {
    preventOverflow: {
      enabled: true,
      boundariesElement: 'window',
    },
  },
};

export const ActionCell = ({
  data,
  currentDirectory,
  isMobile = false,
  onSelectAction,
  canEdit = false,
  canDelete = false,
  ...rest
}: CellProps & {
  currentDirectory: Folder;
  isMobile?: boolean;
  onSelectAction(value: any, data: any): void;
  canEdit?: boolean;
  canDelete?: boolean;
}) => {
  const { t } = useTranslation();
  const { onHoverOut, onHoverIn, dragging, patching, dragItem } =
    useDragAndDropContext();

  const [hoveredIn, setHoveredIn] = useState(false);

  const isDragging = dragging || patching;
  const isActive = dragItem?.id === data.id;

  const isHovered = (isActive && !isDragging) || hoveredIn;

  const options = useMemo(() => {
    if (!isAutomationData(data)) {
      return [
        canEdit && { value: actions.edit_folder, label: t('Edit') },
        canDelete && { value: actions.delete_folder, label: t('Delete') },
      ].filter(Boolean);
    }

    if (
      isTypeRecordBased(data) &&
      (data.customObject.deleted || !data.customObject.access.view)
    ) {
      return [
        !isMobile && { value: actions.history, label: t('History') },
        !isMobile &&
          canDelete && { value: actions.delete, label: t('Archive') },
      ].filter(Boolean);
    }

    return [
      !isMobile && canEdit && { value: actions.edit, label: t('Edit') },
      !isMobile &&
        canEdit && {
          value: actions.edit_automation_folder,
          label: t('Edit Folder'),
        },
      !isMobile &&
        canEdit && {
          value: actions.edit_description,
          label: t('Edit Description'),
        },
      !isMobile &&
        data.active &&
        isTypeGlobal(data) && { value: actions.run, label: t('Run Now') },
      data.active
        ? canEdit && { value: actions.deactivate, label: t('Deactivate') }
        : canEdit && { value: actions.activate, label: t('Activate') },
      !isMobile &&
        canEdit && { value: actions.duplicate, label: t('Duplicate') },
      !isMobile && { value: actions.history, label: t('History') },
      !isMobile && canDelete && { value: actions.delete, label: t('Archive') },
    ].filter(Boolean);
  }, [isMobile, data, canEdit, canDelete, t]);

  if (!options.length) {
    return <StyledNoActionsCell />;
  }

  const title = isAutomationData(data)
    ? t('Edit Agentic Workflow')
    : t('Edit Folder');

  const onMouseEnter = () => {
    setHoveredIn(true);
    onHoverOut();
  };

  const onMouseLeave = () => {
    setHoveredIn(false);
    onHoverIn(data);
  };

  const handleSelectAction = (value: any, data: any) => {
    onSelectAction(value, data);
    setHoveredIn(false);
  };

  return (
    <StyledActionCell
      title={title}
      menuType="absolute"
      options={options}
      data={data}
      width={columnSize.actions}
      padding={paddingSize.small}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      popperConfig={POPPER_CONFIG}
      onSelectAction={handleSelectAction}
      {...rest}
      isHovered={isHovered}
    />
  );
};

type ResizableHeadCellProps = {
  column: { id: AutomationColumnId };
  label: string;
  padding: string;
};

export const ResizableHeadCell: FC<
  PropsWithChildren<ResizableHeadCellProps>
> = ({ label, padding, children, column, ...others }) => {
  const { columnsConfig, setColumnsConfig, resizing, setResizing } =
    useContext(ColumnsContext);
  const pageConfig = useSelector(
    (state: any) => state.automationPage.pageConfig
  );
  const dispatch = useDispatch();

  const onStart = useCallback(() => {
    setResizing(column.id);
  }, [setResizing, column.id]);

  const handleResize: DraggableEventHandler = useCallback(
    (ev, { x }) => {
      const nextWidth = Math.max(
        minColumnWidth[column.id],
        x - CELL_PADDING / 2
      );
      setColumnsConfig((prev) => ({
        ...prev,
        [column.id]: { ...prev[column.id], width: nextWidth },
      }));
    },
    [column.id, setColumnsConfig]
  );

  const onStop = useCallback(() => {
    setResizing(null);
    setTimeout(() => {
      const nextConfig = {
        ...pageConfig,
        columns: columnsConfig,
      };
      TeamMemberService.setPageConfig('automation_list', nextConfig);
      dispatch(updatePageConfig(nextConfig));
    });
  }, [pageConfig, columnsConfig, dispatch, setResizing]);

  return (
    <StyledSortableHeadCell
      className={resizing === column.id ? 'Resizing' : ''}
      resizing={resizing}
      column={column}
      padding={padding}
      width={`${columnsConfig[column.id].width}px`}
      label={label}
      {...others}
    >
      {children}
      <ResizerLayout className="ResizerLayout" padding={CELL_PADDING}>
        <DraggableCore onDrag={handleResize} onStart={onStart} onStop={onStop}>
          <Icon className="Resizer" icon="column-resize" size="intrinsic" />
        </DraggableCore>
      </ResizerLayout>
    </StyledSortableHeadCell>
  );
};
